import { resolveDestination } from "routing/to";
import { Menu as MuiMenu, useTheme } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { useLocation } from "react-router";
import { AccountMenuProps } from "./AccountMenu.props";
import { makePaperStyles } from "./AccountMenu.styles";
import { AccountBlock } from "../AccountBlock";
import { AccountMenuButton, AccountMenuLink } from "../AccountMenuButton";
import { Divider } from "../Divider";
import { useLogout, useAccountNavigationConfig, useAuthState } from "../../../hooks";
import { ShopSwitch } from "../ShopSwitch";
import { withFeatureVisibility } from "../../../HoC";

export const AccountMenu = ({
  anchorEl,
  onClose,
  onNavigate,
  shopId,
  userId,
  withShopNavigation = false,
  questionnaireIncomplete = false
}: AccountMenuProps) => {
  const theme = useTheme();

  const { t } = useTranslation();

  const { pathname } = useLocation();

  const accountMenuRoutes = useAccountNavigationConfig();

  const { checkPermission } = useAuthState();

  const open = Boolean(anchorEl);

  const badgesConfig: Record<string, boolean> = {
    questionnaireIncomplete
  };

  const handleLogout = useLogout();

  const parsedButtons = useMemo(
    () =>
      accountMenuRoutes.map(
        ({ translationKey, path, badgeKey, requiredPermission, featureFlag }) => {
          if (requiredPermission && !checkPermission(requiredPermission)) return null;

          const active = pathname === resolveDestination(path, shopId);
          const badge = badgeKey ? badgesConfig[badgeKey] : undefined;
          const to = resolveDestination(path, shopId);
          if (to === undefined) return null;

          if (featureFlag) {
            const Component = withFeatureVisibility(AccountMenuLink, featureFlag);

            return (
              <Component active={active} key={translationKey} showBadge={badge} to={to}>
                {t(translationKey)}
              </Component>
            );
          }

          return (
            <AccountMenuLink
              active={active}
              key={translationKey}
              onClick={onNavigate}
              showBadge={badge}
              to={to}
            >
              {t(translationKey)}
            </AccountMenuLink>
          );
        }
      ),
    [accountMenuRoutes, badgesConfig, checkPermission, onNavigate, pathname, shopId, t]
  );

  return (
    <MuiMenu
      anchorEl={anchorEl}
      open={open}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left"
      }}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "left"
      }}
      slotProps={{
        paper: {
          sx: makePaperStyles(theme)
        }
      }}
      onClose={onClose}
    >
      <AccountBlock userId={userId} shopId={withShopNavigation ? shopId?.toString() : undefined} />
      <Divider />
      {withShopNavigation && <ShopSwitch />}
      {parsedButtons}
      <Divider />
      <AccountMenuButton
        onClick={handleLogout}
        iconPosition="start"
        icon={<ExitToAppIcon viewBox={"0 0 22 22"} />}
      >
        {t("navigation.accountMenu.logout")}
      </AccountMenuButton>
    </MuiMenu>
  );
};
